import { computed, Signal, signal } from '@angular/core';
import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { Filter } from '@shared/components/data-view/data-view-types';
import { AppliedFilter, AppliedFilterSchema, FilterDef, FilterQueryInput } from '@shared/dataview/core/dataview-types';
import { buildRequestFilters, toQueryInputs } from '@shared/dataview/core/query-builder';
import { decodeFilterParams, UrlParams } from '@shared/dataview/core/url-codec';
import { isEqual } from 'lodash';

export interface DataViewFilterStoreOptions {
  definitions: Signal<FilterDef[]>;
  implicit?: Signal<FilterQueryInput[]>;
}

export class DataViewFilterStore {
  readonly definitions: Signal<FilterDef[]>;
  readonly definitionsById: Signal<ReadonlyMap<string, FilterDef>>;
  readonly applied = signal<AppliedFilter[]>([]);
  readonly chips: Signal<Filter.AppliedDataViewFilter[]>;
  readonly requestFilters: Signal<OpenSearchRequestFilter[]>;

  constructor(options: DataViewFilterStoreOptions) {
    this.definitions = options.definitions;
    this.definitionsById = computed(() => new Map(this.definitions().map(def => [def.id, def])));

    this.chips = computed(
      () => {
        const byId = this.definitionsById();
        return this.applied().flatMap(applied => {
          const def = byId.get(applied.id);
          return def ? [{ ...def, ...applied }] : [];
        });
      },
      // app-data-view-filters edits the chips it is given in place, so chips that already match are kept as they are.
      { equal: (shown, next) => isEqual(shown.map(toApplied), next.map(toApplied)) },
    );

    this.requestFilters = computed(
      () => {
        const inputs = toQueryInputs(this.applied(), this.definitionsById());
        return buildRequestFilters([...inputs, ...(options.implicit?.() ?? [])]);
      },
      { equal: isEqual },
    );
  }

  set(filter: AppliedFilter): void {
    this.applied.update(filters => {
      const index = filters.findIndex(existing => existing.id === filter.id);
      if (index === -1) return [...filters, filter];
      const next = [...filters];
      next[index] = filter;
      return next;
    });
  }

  applyChips(chips: Filter.AppliedDataViewFilter[]): void {
    this.applied.set(chips.map(toApplied));
  }

  clear(): void {
    const byId = this.definitionsById();
    this.applied.update(filters => filters.filter(filter => byId.get(filter.id)?.required));
  }

  applyFromSchema(schema: AppliedFilterSchema[]): void {
    this.applied.set(this.resolve(schema));
  }

  applyFromUrl(params: UrlParams): void {
    const { filters } = decodeFilterParams(params);
    for (const filter of this.resolve(filters)) this.set(filter);
  }

  toSchema(): AppliedFilterSchema[] {
    return this.applied().map(toFilterSchema);
  }

  canonicalize(schema: AppliedFilterSchema[]): AppliedFilterSchema[] {
    return this.resolve(schema).map(toFilterSchema);
  }

  private resolve(schema: AppliedFilterSchema[]): AppliedFilter[] {
    const byId = this.definitionsById();
    const resolved: AppliedFilter[] = [];

    for (const entry of schema) {
      const def = byId.get(entry.id);
      if (!def) continue;

      const type = entry.type && def.allowedTypes.includes(entry.type) ? entry.type : def.allowedTypes[0];
      if (!type) continue;

      resolved.push({ id: entry.id, type, value: entry.value, timezone: entry.timezone });
    }

    return resolved;
  }
}

function toApplied({ id, type, value, timezone }: AppliedFilter): AppliedFilter {
  return { id, type, value: value ?? undefined, timezone };
}

function toFilterSchema(filter: AppliedFilter): AppliedFilterSchema {
  const schema: AppliedFilterSchema = { id: filter.id, value: filter.value, type: filter.type };
  if (filter.timezone !== undefined) schema.timezone = filter.timezone;
  return schema;
}
